import type { BrowserOptions, ErrorBoundaryProps } from '@sentry/react'
import * as Sentry from '@sentry/react'
import {
    breadcrumbsIntegration,
    browserTracingIntegration,
    dedupeIntegration,
    globalHandlersIntegration,
    httpClientIntegration,
    linkedErrorsIntegration,
    replayIntegration
} from '@sentry/react'
import { SentryFallback } from '../components/SentryFallback'
import { getEnvVar } from '../config'
import { ROUTE_SAMPLING_RATES, SENSITIVE_PATTERNS } from './sentry-constants'

// Environment-based configuration
const environment = getEnvVar('VITE_APP_ENV', 'development')
const isProduction = environment === 'production'
const release = getEnvVar('VITE_APP_VERSION', 'unknown')
const sentryDsn = getEnvVar('VITE_SENTRY_DSN', '')

const errorSampleRate = parseFloat(getEnvVar('VITE_SENTRY_ERROR_SAMPLE_RATE', '0.3'))
const replaysSessionSampleRate = parseFloat(getEnvVar('VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE', '0.1'))
const replaysOnErrorSampleRate = parseFloat(getEnvVar('VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE', '0.1'))

// AIE-120: Enable performance monitoring in production with conservative sampling
// Environment variable approach allows runtime configuration without code changes
const tracesSampleRateEnv = parseFloat(getEnvVar('VITE_SENTRY_TRACES_SAMPLE_RATE', '0.005'))
const enablePerformanceMonitoring = true  // Changed from: !isProduction
const enableSessionReplay = !isProduction



// Error filtering function
function beforeSend(event: Sentry.Event, hint: Sentry.EventHint): Sentry.Event | null {
  // Filter out development errors if in production
  if (environment === 'production' && hint.originalException) {
    const error = hint.originalException as Error
    if (error.stack?.includes('localhost') || error.stack?.includes('127.0.0.1')) {
      return null
    }
  }

  // Filter out browser extension errors
  if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
    frame => frame.filename?.includes('extension://') ||
             frame.filename?.includes('chrome-extension://') ||
             frame.filename?.includes('moz-extension://') ||
             frame.filename?.includes('safari-extension://')
  )) {
    return null
  }

  // Filter out known third-party script errors
  const ignoredScripts = ['googletagmanager', 'analytics', 'hotjar', 'gtag', 'google-analytics', 'facebook', 'fbevents']
  const hasThirdPartyError = event.exception?.values?.[0]?.stacktrace?.frames?.some(
    frame => ignoredScripts.some(script => frame.filename?.includes(script))
  )
  if (hasThirdPartyError) {
    return null
  }

  // Filter out network errors that are expected (e.g., user offline)
  if (event.exception?.values?.[0]?.type === 'NetworkError') {
    return null
  }

  // Filter out expected auth/connection errors and operational noise
  const errorMessage = event.exception?.values?.[0]?.value || event.message || ''
  const errorType = event.exception?.values?.[0]?.type || ''
  const expectedErrorPatterns = [
    /429/,
    /Sentry dropped data due to a quota/,
    /401/,
    /403/,
    /unauthorized/i,
    /signature.*expired/i,
    /token.*expired/i,
    /failed to connect.*401/i,
    // ✅ OPTIMIZATION: Filter operational events, not errors
    /circuit breaker/i,
    /threshold reached/i,
    /opening circuit/i,
    /circuit.*opened/i,
    /circuit.*closed/i,
    /half.?open/i,
    // ✅ OPTIMIZATION: Filter async cleanup warnings
    /task was destroyed but it is pending/i,
    /task.*pending/i,
    // ✅ OPTIMIZATION: Filter success events
    /validation passed/i,
    /insights generated/i,
    /no studies returned/i,
    // FRONTEND-A3: DOM Range selection errors from third-party libraries
    // These occur when Plotly/D3 or React internal code tries to select a DOM node
    // that has been unmounted. Edge browser specific, harmless race condition.
    /InvalidNodeTypeError/i,
    /the given Node has no parent/i,
    /Failed to execute 'selectNode' on 'Range'/i,
  ]

  // Also check error type for InvalidNodeTypeError
  if (errorType === 'InvalidNodeTypeError') {
    return null
  }

  if (expectedErrorPatterns.some(pattern => pattern.test(errorMessage))) {
    return null
  }

  // Sanitize sensitive data from the event
  if (event.request) {
    event.request = sanitizeRequestData(event.request)
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(sanitizeBreadcrumb)
  }

  if (event.contexts) {
    event.contexts = sanitizeContexts(event.contexts)
  }

  return event
}

// Sanitize request data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeRequestData(request: any): any {
  const sanitized = { ...request }

  // Sanitize headers
  if (sanitized.headers) {
    Object.keys(sanitized.headers).forEach(key => {
      if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
        sanitized.headers[key] = '[REDACTED]'
      }
    })
  }

  // Sanitize query string
  if (sanitized.query_string) {
    sanitized.query_string = sanitizeString(sanitized.query_string)
  }

  // Sanitize data
  if (sanitized.data) {
    sanitized.data = sanitizeObject(sanitized.data)
  }

  return sanitized
}

// Sanitize breadcrumb data
function sanitizeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  const sanitized = { ...breadcrumb }

  if (sanitized.data) {
    sanitized.data = sanitizeObject(sanitized.data)
  }

  if (sanitized.message) {
    sanitized.message = sanitizeString(sanitized.message)
  }

  return sanitized
}

// Sanitize context data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeContexts(contexts: Record<string, any>): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitized: Record<string, any> = {}

  Object.entries(contexts).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value)
    } else {
      sanitized[key] = value
    }
  })

  return sanitized
}

// Sanitize object recursively
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeObject(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitized: Record<string, any> = {}

  Object.entries(obj).forEach(([key, value]) => {
    if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
      sanitized[key] = '[REDACTED]'
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value)
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value)
    } else {
      sanitized[key] = value
    }
  })

  return sanitized
}

// Sanitize string values
function sanitizeString(str: string): string {
  // Check if the string looks like it contains sensitive data
  if (SENSITIVE_PATTERNS.some(pattern => pattern.test(str))) {
    return '[REDACTED]'
  }
  return str
}

// Dynamic sampling based on route
function tracesSampler(samplingContext: { transactionContext?: { name?: string } }): number {
  // For transactions
  if (samplingContext.transactionContext) {
    const transactionName = samplingContext.transactionContext.name
    
    // Check route-based sampling
    for (const [route, rate] of Object.entries(ROUTE_SAMPLING_RATES)) {
      if (transactionName && transactionName.includes(route)) {
        return rate
      }
    }
  }

  // Default sampling rate - reduced to conserve quota
  return enablePerformanceMonitoring ? 0.001 : 0
}

// Breadcrumb configuration
function beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  // Filter out noisy console logs
  if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
    return null
  }

  // Filter out frequent/noisy navigation events
  if (breadcrumb.category === 'navigation' && breadcrumb.data?.to === breadcrumb.data?.from) {
    return null
  }

  // Enhance fetch/XHR breadcrumbs with more context
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    const url = breadcrumb.data?.url
    if (url) {
      // Add API endpoint categorization
      if (url.includes('/api/')) {
        breadcrumb.data = {
          ...breadcrumb.data,
          api_endpoint: url.replace(/.*\/api\//, '/api/'),
        }
      }
    }
  }

  return breadcrumb
}

// Sentry configuration options
const integrations = [
  // NOTE: captureConsoleIntegration disabled because our logger already properly
  // sends errors to Sentry via Sentry.captureException/captureMessage.
  // Enabling this was causing duplicate error reports with console formatting codes
  // (%c) appearing in error titles.
  // captureConsoleIntegration({
  //   levels: ['error'],
  // }),
  httpClientIntegration({
    failedRequestStatusCodes: [500, 599],
    failedRequestTargets: [/.*/],
  }),
  breadcrumbsIntegration({
    console: true,
    dom: true,
    fetch: true,
    history: true,
    xhr: true,
  }),
  globalHandlersIntegration({
    onerror: true,
    onunhandledrejection: true,
  }),
  linkedErrorsIntegration({
    key: 'cause',
    limit: 5,
  }),
  dedupeIntegration(),
]

if (enablePerformanceMonitoring) {
  integrations.unshift(browserTracingIntegration())
}

if (enableSessionReplay) {
  integrations.unshift(replayIntegration())
}

export const sentryConfig: BrowserOptions = {
  dsn: sentryDsn,
  environment,
  release,
  // Reduce error sample rate to conserve quota while preserving critical visibility
  sampleRate: Number.isNaN(errorSampleRate) ? 0.3 : Math.min(Math.max(errorSampleRate, 0), 1),
  
  // Client-side rate limiting to avoid 429 errors
  transportOptions: {
    // Additional transport options can be added here
  },
  
  // Handle rate limiting errors gracefully
  beforeSendTransaction(event) {
    if (!enablePerformanceMonitoring) {
      return null
    }
    return event
  },

  // Performance Monitoring
  integrations,

  // Performance configuration
  // AIE-120: Use environment variable for flexible sampling (default 0.5%)
  // Conservatively sampled to track Web Vitals without exhausting quota
  tracesSampleRate: enablePerformanceMonitoring ? tracesSampleRateEnv : 0,
  tracesSampler,

  // Session Replay
  // Reduced session replay rates to conserve quota
  replaysSessionSampleRate: enableSessionReplay
    ? (isNaN(replaysSessionSampleRate) ? 0.1 : replaysSessionSampleRate)
    : 0,
  // Reduced from 1.0 to 0.1 (10%) to conserve quota
  replaysOnErrorSampleRate: enableSessionReplay
    ? (isNaN(replaysOnErrorSampleRate) ? 0.1 : replaysOnErrorSampleRate)
    : 0,
  
  // Release health tracking
  autoSessionTracking: true,
  
  // Breadcrumbs configuration
  maxBreadcrumbs: 50,
  beforeBreadcrumb,
  
  // Error filtering and sanitization
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  beforeSend: beforeSend as any,
  
  // Ignore certain errors
  ignoreErrors: [
    // Browser extensions
    'top.GLOBALS',
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    // Network errors that are expected
    'NetworkError',
    'Failed to fetch',
    // React errors that are handled
    'ChunkLoadError',
    'Loading chunk',
    // Sentry rate limiting
    'Sentry dropped data due to a quota',
    '429',
    // ✅ OPTIMIZATION: Operational events, not errors
    /circuit breaker/i,
    /threshold reached/i,
    // ✅ OPTIMIZATION: Async cleanup warnings
    /task was destroyed but it is pending/i,
    // ✅ OPTIMIZATION: Success events that shouldn't be in Sentry
    /validation passed/i,
    /insights generated/i,
    // FRONTEND-A3: DOM Range selection errors from third-party libraries (Plotly/D3)
    // These occur when React unmounts components before selection operations complete
    // Edge browser specific, harmless race condition
    /InvalidNodeTypeError/i,
    /the given Node has no parent/i,
    /Failed to execute 'selectNode' on 'Range'/i,
  ],
  
  // Deny URLs for error tracking
  denyUrls: [
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Firefox extensions
    /^resource:\/\//i,
    // Other browser extensions
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
  ],
  
  // User context
  initialScope: {
    tags: {
      component: 'frontend',
      framework: 'react',
    },
  },

  // Enable geo-location tracking via IP address
  // Sentry will automatically perform geo-IP lookup server-side
  sendDefaultPii: true,

  // Debug mode in development
  debug: !isProduction,

  // Attach stack traces to messages
  attachStacktrace: false,

  // Shutdown timeout
  shutdownTimeout: 2000,
}

// Error boundary configuration
export const errorBoundaryConfig: ErrorBoundaryProps = {
  fallback: ({ error, resetError }) => (
    <SentryFallback error={error as Error} resetError={resetError} />
  ),
  showDialog: environment === 'development',
}

// Initialize Sentry
export function initSentry() {
  if (!sentryDsn) {
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.')
    return
  }

  Sentry.init(sentryConfig)

  // Set initial user context if available
  const userId = localStorage.getItem('userId')
  if (userId) {
    Sentry.setUser({ id: userId })
  }

  // Add custom tags
  Sentry.setTag('app.version', release)
  Sentry.setTag('app.environment', environment)

  console.log(`Sentry initialized for ${environment} environment`)
}

// Export utility functions for use in the app
export {
    addBreadcrumb, captureException,
    captureMessage, setContext, setExtra, setTag, setUser, withScope
} from '@sentry/react'

// Re-export the entire Sentry namespace
export { Sentry }

// Helper to capture exceptions with additional context
export function captureExceptionWithContext(
  error: Error,
  context: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'error'
) {
  Sentry.withScope(scope => {
    // Set level
    scope.setLevel(level)
    
    // Add context
    Object.entries(context).forEach(([key, value]) => {
      scope.setContext(key, value as Record<string, unknown>)
    })
    
    // Capture the exception
    Sentry.captureException(error)
  })
}

// Helper to add performance metrics
export function measurePerformance(
  name: string,
  fn: () => void | Promise<void>
): void | Promise<void> {
  const startTime = performance.now()
  
  try {
    const result = fn()
    
    if (result instanceof Promise) {
      return result.finally(() => {
        const duration = performance.now() - startTime
        Sentry.addBreadcrumb({
          message: `Performance: ${name} took ${duration.toFixed(2)}ms`,
          category: 'performance',
          level: 'info',
        })
      })
    }
    
    const duration = performance.now() - startTime
    Sentry.addBreadcrumb({
      message: `Performance: ${name} took ${duration.toFixed(2)}ms`,
      category: 'performance',
      level: 'info',
    })
    return result
  } catch (error) {
    const duration = performance.now() - startTime
    Sentry.addBreadcrumb({
      message: `Performance: ${name} failed after ${duration.toFixed(2)}ms`,
      category: 'performance',
      level: 'error',
    })
    throw error
  }
}
